import {
  CircleDollarSign,
  CreditCard,
  Receipt,
  ShoppingCart,
  Undo2,
  WalletCards,
} from 'lucide-react'
import { MoneyValue, StatCard } from '@shared/design-system/ui'
import type {
  DashboardCurrencyOverview,
  DashboardOverview,
  DashboardView,
  RevenuePoint,
} from '../api/dashboard.contract'
import { kpiGrid, kpiHero, kpiWide, sparkline, sparklineLine } from './dashboard.css'

interface DeltaView {
  label: string
  positive: boolean
  tone?: 'positive' | 'negative' | 'neutral'
}

interface KpiRowProps {
  currencyData: DashboardCurrencyOverview
  global: DashboardOverview['global']
  view: DashboardView
}

function formatSignedPercent(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  const formatted = Math.abs(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
  return `${sign}${formatted}%`
}

function formatSignedPoints(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  const formatted = Math.abs(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
  return `${sign}${formatted} pp`
}

function moneyDelta(deltaPct: number | undefined, positive: boolean | undefined): DeltaView | null {
  if (deltaPct === undefined) {
    return null
  }
  return {
    label: `${formatSignedPercent(deltaPct)} vs período anterior`,
    positive: positive ?? deltaPct >= 0,
  }
}

function rateDelta(deltaPp: number | undefined, positive: boolean | undefined): DeltaView | null {
  if (deltaPp === undefined) {
    return null
  }
  return {
    label: `${formatSignedPoints(deltaPp)} vs período anterior`,
    positive: positive ?? deltaPp >= 0,
  }
}

function neutralRateDelta(
  deltaPp: number | undefined,
  positive: boolean | undefined,
): DeltaView | null {
  const value = rateDelta(deltaPp, positive)
  if (!value) {
    return null
  }
  return { ...value, tone: 'neutral' }
}

function formatInteger(value: number): string {
  return value.toLocaleString('pt-BR')
}

function formatRate(value: number): string {
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

function seriesValues(data: RevenuePoint[], key: 'venda' | 'recebido'): number[] {
  return data.map((point) => Number(point[key].amount)).filter((value) => Number.isFinite(value))
}

function MiniSparkline({ values }: { values: number[] }) {
  if (values.length < 2) {
    return null
  }

  const min = Math.min(...values)
  const max = Math.max(...values)
  const spread = max - min || 1
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 64
      const y = 24 - ((value - min) / spread) * 20 - 2
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')

  return (
    <svg className={sparkline} viewBox="0 0 64 24" aria-hidden="true" focusable="false">
      <polyline className={sparklineLine} points={points} />
    </svg>
  )
}

export function KpiRow({ currencyData, global, view }: KpiRowProps) {
  const faturamentoDelta = moneyDelta(
    currencyData.faturamento.liquido.deltaPct,
    currencyData.faturamento.liquido.deltaPositive,
  )
  const recebidoDelta = moneyDelta(
    currencyData.recebido.deltaPct,
    currencyData.recebido.deltaPositive,
  )
  const ticketDelta = moneyDelta(
    currencyData.ticketMedio.deltaPct,
    currencyData.ticketMedio.deltaPositive,
  )
  const salesDelta = moneyDelta(global.salesCount.deltaPct, global.salesCount.deltaPositive)
  const approvalDelta = rateDelta(
    global.cardApprovalPct.deltaPp,
    global.cardApprovalPct.deltaPositive,
  )
  const refundDelta = neutralRateDelta(
    global.refundRatePct.deltaPp,
    global.refundRatePct.deltaPositive,
  )
  const vendaSeries = seriesValues(currencyData.revenueSeries, 'venda')
  const recebidoSeries = seriesValues(currencyData.revenueSeries, 'recebido')

  return (
    <div className={kpiGrid}>
      <StatCard
        hero
        className={kpiHero}
        label="Líquido sem reserva"
        helper="Antes das perdas e ajustes. Em Vendas, o líquido inclui a reserva."
        amount={currencyData.faturamento.liquido.value.amount}
        currency={currencyData.faturamento.liquido.value.currency}
        icon={<CircleDollarSign size={18} />}
        sparkline={<MiniSparkline values={vendaSeries} />}
        breakdown={
          <>
            <span>
              Bruto{' '}
              <MoneyValue
                amount={currencyData.faturamento.bruto.amount}
                currency={currencyData.faturamento.bruto.currency}
                size="sm"
              />
            </span>
          </>
        }
        delta={faturamentoDelta ?? undefined}
      />

      <StatCard
        label="Recebido no período"
        amount={currencyData.recebido.value.amount}
        currency={currencyData.recebido.value.currency}
        icon={<WalletCards size={18} />}
        helper="Confirmado por data de pagamento, sem a reserva"
        sparkline={<MiniSparkline values={recebidoSeries} />}
        delta={recebidoDelta ?? undefined}
      />

      <StatCard
        label={view === 'renewals' ? 'Ticket médio da renovação' : 'Ticket médio da venda'}
        amount={currencyData.ticketMedio.value.amount}
        currency={currencyData.ticketMedio.value.currency}
        icon={<Receipt size={18} />}
        helper={view === 'renewals' ? 'Por renovação cobrada' : 'Por jornada de compra'}
        sparkline={<MiniSparkline values={vendaSeries} />}
        delta={ticketDelta ?? undefined}
      />

      <StatCard
        className={kpiWide}
        label={view === 'renewals' ? 'Renovações cobradas' : 'Vendas criadas'}
        value={<span className="vp-tabular">{formatInteger(global.salesCount.value)}</span>}
        icon={<ShoppingCart size={18} />}
        helper={`${formatInteger(global.chargeCount.value)} ${global.chargeCount.value === 1 ? 'cobrança aprovada' : 'cobranças aprovadas'}`}
        delta={salesDelta ?? undefined}
      />

      <StatCard
        label="Aprovação cartão"
        value={<span className="vp-tabular">{formatRate(global.cardApprovalPct.value)}</span>}
        icon={<CreditCard size={18} />}
        helper="Pagas sobre pagas + falhas"
        delta={approvalDelta ?? undefined}
      />

      <StatCard
        label="Taxa de reembolso"
        value={<span className="vp-tabular">{formatRate(global.refundRatePct.value)}</span>}
        icon={<Undo2 size={18} />}
        helper="Estornos regulares sobre cobranças aprovadas"
        delta={refundDelta ?? undefined}
      />
    </div>
  )
}
